import { useState } from 'react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

/**
 * One table implementation for the whole console. Sorting, filtering and
 * pagination come from TanStack Table rather than being re-derived per page —
 * hand-rolled sort comparators are where staff tools quietly start lying.
 */
export function DataTable<T>({
  data,
  columns,
  filterPlaceholder,
  emptyMessage,
  pageSize = 50,
}: {
  readonly data: readonly T[]
  readonly columns: readonly ColumnDef<T, unknown>[]
  readonly filterPlaceholder?: string
  readonly emptyMessage: string
  readonly pageSize?: number
}) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  const table = useReactTable({
    data: data as T[],
    columns: columns as ColumnDef<T, unknown>[],
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  })

  const pageCount = table.getPageCount()

  return (
    <div>
      {filterPlaceholder ? (
        <label className="print-hide relative mb-2 flex w-64 items-center">
          <Search size={14} className="pointer-events-none absolute left-2.5 opacity-50" aria-hidden />
          <input
            className="st-input st-input-search w-full"
            type="search"
            placeholder={filterPlaceholder}
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            aria-label={filterPlaceholder}
          />
        </label>
      ) : null}

      <div className="st-card overflow-x-auto">
        <table className="st-table">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const direction = header.column.getIsSorted()
                  return (
                    <th
                      key={header.id}
                      className={canSort ? 'st-sortable' : undefined}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
                    >
                      <span className="inline-flex items-center gap-1">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {direction === 'asc' ? <ChevronUp size={12} aria-hidden /> : null}
                        {direction === 'desc' ? <ChevronDown size={12} aria-hidden /> : null}
                      </span>
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={table.getAllLeafColumns().length} className="st-meta py-8 text-center">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 ? (
        <div className="print-hide mt-2 flex items-center gap-2 text-sm">
          <button type="button" className="st-btn-ghost" disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>
            Previous
          </button>
          <span className="st-meta tabular-nums">
            Page {table.getState().pagination.pageIndex + 1} of {pageCount}
          </span>
          <button type="button" className="st-btn-ghost" disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>
            Next
          </button>
        </div>
      ) : null}
    </div>
  )
}
